// src/authHandlers/ProtectedRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import LoadingPage from "../pages/Loading";
import PropTypes from "prop-types";
import { hasAllowedRole } from "../utils/roleUtils";
import { LEGACY_HERO_DASHBOARD_ROLES } from "../financialDashboard/financialRoleCapabilities";

// Allowed states for learner dashboard access
const ALLOWED_STATES = [
  "Applicant",
  "Learner",
  "ProfileComplete",
  "Free Trial",
  "Free trial", // Also allow lowercase version
];

const ProtectedRoute = ({
  children,
  allowedRoles = [],
  loginRoute = "/hero-login-manual",
}) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userState, setUserState] = useState(null);
  const [isCompleted, setIsCompleted] = useState(null);
  const [firestoreRole, setFirestoreRole] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);

        // Check if the user's email is verified
        if (!currentUser.emailVerified) {
          setLoading(false);
          return; // Do not proceed further
        }

        // Get the custom claims to check the user's role
        const functions = getFunctions();
        const getCustomClaims = httpsCallable(functions, "getCustomClaims");
        try {
          const result = await getCustomClaims();
          const claims = result.data;

          setUserRole(claims.role);
          setUserRoles(
            [
              claims.role,
              ...(Array.isArray(claims.roles) ? claims.roles : []),
            ].filter(Boolean),
          );

          const isHeroRole = allowedRoles.some((role) =>
            LEGACY_HERO_DASHBOARD_ROLES.includes(role)
          );

          if (isHeroRole) {
            try {
              const firestoreReadDocSafe = httpsCallable(
                functions,
                "firestoreReadDocSafe",
              );
              const userDocResult = await firestoreReadDocSafe({
                company: "TPC_Group",
                collectionBrand: "TechPerformanceCentre",
                systemName: "Users",
                uniqueId: currentUser.email,
              });

              if (userDocResult.data?.found) {
                const userData = userDocResult.data.data;
                setFirestoreRole(userData.role);
              } else {
                setFirestoreRole(null);
              }
            } catch (error) {
              console.error("Error fetching Firestore role:", error);
              setFirestoreRole(null);
            }
          }

          // Check user state in the database for learner dashboard access
          if (
            allowedRoles.includes("learner") ||
            allowedRoles.includes("applicant") ||
            allowedRoles.includes("freetrial") ||
            allowedRoles.includes("bursaryLearner")
          ) {
            try {
              const firestoreReadDocSafe = httpsCallable(
                functions,
                "firestoreReadDocSafe",
              );
              const userDocResult = await firestoreReadDocSafe({
                company: "TPC_Group",
                collectionBrand: "TechPerformanceCentre",
                systemName: "Applicants",
                uniqueId: currentUser.email,
              });

              if (userDocResult.data?.found) {
                const userData = userDocResult.data.data;
                setUserState(userData.state);
                setIsCompleted(userData.isCompleted);
              } else {
                setUserState(null);
                setIsCompleted(null);
              }
            } catch (error) {
              setUserState(null);
              setIsCompleted(null);
            }
          }
        } catch (error) {
          console.error("Error fetching custom claims:", error);
        }
      } else {
        setUser(null);
        setUserRole(null);
        setUserRoles([]);
        setUserState(null);
        setFirestoreRole(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [allowedRoles]);

  if (loading) {
    return <LoadingPage />;
  }

  if (!user || !user.emailVerified) {
    return <Navigate to={loginRoute} replace />;
  }

  // Only perform role/state checks if allowedRoles are specified
  if (allowedRoles.length > 0) {
    const isLearnerArea =
      allowedRoles.includes("learner") ||
      allowedRoles.includes("applicant") ||
      allowedRoles.includes("freetrial") ||
      allowedRoles.includes("bursaryLearner");
    const hasHeroDashboardAccess = userRoles.some((role) =>
      String(role).startsWith("Hero-"),
    );
    const hasFacilitatorDashboardAccess = hasAllowedRole(userRoles, [
      "Facilitator",
    ]);
    const hasLearnerDashboardAccess = hasAllowedRole(userRoles, [
      "learner",
      "applicant",
      "freetrial",
      "bursaryLearner",
    ]);

    // Check if user is a facilitator by Firestore role
    if (
      allowedRoles.includes("Facilitator") &&
      (firestoreRole === "Facilitator" || hasFacilitatorDashboardAccess)
    ) {
      return children;
    }

    // Admin-only routes (e.g. admin-portal): only Admin role allowed
    const isAdminOnly =
      allowedRoles.length === 1 && allowedRoles[0] === "Admin";
    if (isAdminOnly) {
      if (firestoreRole === "Admin") return children;
      return <Navigate to={loginRoute} replace />;
    }

    // Check if user is a hero dashboard role by Firestore role
    const isHeroDashboardRole = LEGACY_HERO_DASHBOARD_ROLES.includes(firestoreRole);
    const allowsHeroDashboard = allowedRoles.some((role) =>
      LEGACY_HERO_DASHBOARD_ROLES.includes(role)
    );

    if (allowsHeroDashboard && isHeroDashboardRole) {
      return children;
    }
    if (allowsHeroDashboard && hasHeroDashboardAccess) {
      return children;
    }
    if (isLearnerArea && hasLearnerDashboardAccess) {
      return children;
    }

    // NEW: If this is the learner area and the user's Applicants.state is allowed,
    // let them through even if their role claim is missing or stale.
    // Use case-insensitive comparison to handle variations like "Free trial" vs "Free Trial"
    if (
      isLearnerArea &&
      userState &&
      ALLOWED_STATES.some(
        (state) => state.toLowerCase() === userState.toLowerCase(),
      )
    ) {
      return children;
    }

    // Check if user is a bursary learner - allow access regardless of completion status
    if (isLearnerArea && hasAllowedRole(userRole, ["bursaryLearner"])) {
      return children;
    }

    // Otherwise fall back to role-based check
    if (
      !hasAllowedRole(userRole, allowedRoles) &&
      !hasAllowedRole(userRoles, allowedRoles)
    ) {
      return <Navigate to="/unauthorized" replace />;
    }

    // If still in learner area, and we have a state but it is not allowed, block
    // Use case-insensitive comparison to handle variations like "Free trial" vs "Free Trial"
    if (
      isLearnerArea &&
      userState &&
      !ALLOWED_STATES.some(
        (state) => state.toLowerCase() === userState.toLowerCase(),
      )
    ) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

ProtectedRoute.propTypes = {
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  loginRoute: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default ProtectedRoute;
